// UI strings for the week on Tänään and its practice (layout v2, docs/design/SIVUPALSTA.md
// "The week road", "Routes"): the week ledger in the margin, the phone's week row and sheet, the
// "Seuraavaksi" line, "Harjoittele viikkoa N" (/lessons/:id/harjoittele) and Vapaa harjoitus's scope note.
// Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md ("1 sana", "2 sanaa"; sinä-muoto; no internal jargon).
import { defineArea } from '../area';

const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;

export const weekPlanFi = {
  /** The phone's row under the strip: "Viikko 1 · 2/5 · seuraavaksi Kuuntele". */
  row: (week: number, done: number, total: number, next: string | null) =>
    `Viikko ${week} · ${done}/${total}${next ? ` · seuraavaksi ${next}` : ' · kaikki tehty'}`,
  /** The week sheet's and the ledger's heading. */
  title: (week: number, title: string) => (title ? `Viikko ${week} · ${title}` : `Viikko ${week}`),
  /** The ledger as a landmark (screen readers). */
  ledger: 'Viikon askeleet',
  count: (done: number, total: number) => `${done}/${total} tehty`,
  /** A step with its count in the label ("Kuuntele (4)"): a step nothing ticks (fix batch D2). */
  withCount: (name: string, n: number) => `${name} (${n})`,
  /** Who has done a step: "Sinä: tehty · Roosa: ei vielä". */
  who: (name: string, done: boolean) => `${name}: ${done ? 'tehty' : 'ei vielä'}`,
  you: 'Sinä',
  /** A step's line under its name. */
  detail: {
    practice: 'päivän kortit',
    lines: (k: number) => `${n(k, 'repliikki', 'repliikkiä')} sarjasta`,
    /** Under "Kuuntele (4)": the count is already in the name. */
    linesFrom: 'repliikit sarjasta',
    noLines: 'ei sarjan repliikkejä tällä viikolla',
    speak: (lines: number, words: number) =>
      [lines ? n(lines, 'repliikki', 'repliikkiä') : '', words ? n(words, 'sana', 'sanaa') : ''].filter(Boolean).join(' ja ') + ' ääneen',
    together: 'kohtaus yhdessä, kumpikin omassa roolissaan',
  },
  /** "Seuraavaksi: …" — the next step as an action. */
  next: (what: string) => `Seuraavaksi: ${what}`,
  nextStep: {
    lesson: (order: number) => `Oppitunti ${order}`,
    practice: 'Päivän kortit',
    listen: (k: number) => `Kuuntele ${n(k, 'repliikki', 'repliikkiä')}`,
    speak: 'Puhu viikon lauseet ääneen',
    letter: 'Kirjoita viikon kirje',
    together: 'Yhteinen sessio',
    free: 'Vapaa harjoitus',
  },
  /** The road on the phone: a week opens this sheet. */
  practice: {
    title: (week: number) => `Harjoittele viikkoa ${week}`,
    /** The week for you (WeekRow.mine): passed by you, or open; never the road's "takana". */
    status: { passed: 'sinä läpäisit', open: 'avoinna' } as Record<'passed' | 'open', string>,
    cards: 'Kortit',
    cardsMet: (met: number, all: number) => (met === all ? n(all, 'sana', 'sanaa') : `${met} / ${n(all, 'sana', 'sanaa')} tavattu`),
    cardsNone: 'Et ole vielä tavannut tämän viikon sanoja.',
    cardsNoWords: 'Tämän viikon sanat eivät ole vielä avautuneet.',
    /** The fixing action when no word of the week has been met: pass its lesson, or Tänään. */
    passLesson: (order: number) => `Oppitunti ${order}`,
    toToday: 'Tänään',
    lines: 'Repliikit',
    linesNone: 'Tällä viikolla ei ole sarjan repliikkejä.',
    speak: 'Puhu',
    speakNone: 'Tällä viikolla ei ole ääneen luettavaa.',
    lesson: 'Oppitunti',
    locked: 'Tämä viikko ei ole vielä auki.',
    unknown: 'Tätä viikkoa ei löydy.',
    words: 'Viikon sanat',
    wordsMore: (k: number) => `ja ${n(k, 'muu', 'muuta')}`,
    unreleased: (k: number) => `${n(k, 'sana', 'sanaa')} avautuu myöhemmin.`,
    focus: 'Painopiste',
    cardsTitle: (week: number) => `Viikon ${week} kortit`,
    speakTitle: (week: number) => `Viikon ${week} lauseet ääneen`,
    back: (week: number) => `Viikko ${week}`,
    open: 'Avaa',
  },
  /** Vapaa harjoitus: what is practised (a margin note on PC, a row + sheet on the phone). */
  scope: {
    title: 'Harjoiteltavaa',
    weeks: 'Viikot',
    allWeeks: 'Kaikki viikot',
    weeksPicked: (k: number) => n(k, 'viikko', 'viikkoa'),
    pickHint: 'Napauta viikkoa tieltä, niin harjoittelet vain sitä.',
    kinds: 'Korttien lajit',
    kind: {
      'hv-native': 'Sanat',
      'native-hv': 'Valyriaksi',
      'form-drill': 'Muodot',
      sentence: 'Lauseet',
      listen: 'Kuuntelu',
    } as Record<'hv-native' | 'native-hv' | 'form-drill' | 'sentence' | 'listen', string>,
    allKinds: 'kaikki kortit',
    change: 'Muuta',
    emptyFiltered: 'Valinnalla ei löydy vielä kortteja.',
    showAll: 'Harjoittele kaikkia',
    empty: 'Vapaassa harjoituksessa kerrataan sanoja, jotka olet jo nähnyt.',
  },
  /** The notebook on Tänään, behind a disclosure. */
  notebook: { show: 'Näytä', hide: 'Piilota' },
};

export const weekPlanEn: typeof weekPlanFi = {
  row: (week, done, total, next) => `Week ${week} · ${done}/${total}${next ? ` · next: ${next}` : ' · all done'}`,
  title: (week, title) => (title ? `Week ${week} · ${title}` : `Week ${week}`),
  ledger: 'The week’s steps',
  count: (done, total) => `${done}/${total} done`,
  withCount: (name, n) => `${name} (${n})`,
  who: (name, done) => `${name}: ${done ? 'done' : 'not yet'}`,
  you: 'You',
  detail: {
    practice: 'today’s cards',
    lines: (k) => `${k} ${k === 1 ? 'line' : 'lines'} from the shows`,
    linesFrom: 'lines from the shows',
    noLines: 'no lines from the shows this week',
    speak: (lines, words) =>
      'say ' +
      [lines ? `${lines} ${lines === 1 ? 'line' : 'lines'}` : '', words ? `${words} ${words === 1 ? 'word' : 'words'}` : ''].filter(Boolean).join(' and ') +
      ' aloud',
    together: 'a scene together, each in your own role',
  },
  next: (what) => `Next: ${what}`,
  nextStep: {
    lesson: (order) => `Lesson ${order}`,
    practice: 'Today’s cards',
    listen: (k) => `Listen to ${k} ${k === 1 ? 'line' : 'lines'}`,
    speak: 'Say the week’s sentences aloud',
    letter: 'Write the week’s letter',
    together: 'The shared session',
    free: 'Free practice',
  },
  practice: {
    title: (week) => `Practise week ${week}`,
    status: { passed: 'you passed', open: 'open' },
    cards: 'Cards',
    cardsMet: (met, all) => (met === all ? `${all} ${all === 1 ? 'word' : 'words'}` : `${met} / ${all} ${all === 1 ? 'word' : 'words'} met`),
    cardsNone: 'You have not met this week’s words yet.',
    cardsNoWords: 'This week’s words have not opened yet.',
    passLesson: (order) => `Lesson ${order}`,
    toToday: 'Today',
    lines: 'Lines',
    linesNone: 'This week has no lines from the shows.',
    speak: 'Speak',
    speakNone: 'This week has nothing to read aloud.',
    lesson: 'Lesson',
    locked: 'This week is not open yet.',
    unknown: 'This week was not found.',
    words: 'The week’s words',
    wordsMore: (k) => `and ${k} more`,
    unreleased: (k) => `${k} ${k === 1 ? 'word opens' : 'words open'} later.`,
    focus: 'Focus',
    cardsTitle: (week) => `Week ${week} cards`,
    speakTitle: (week) => `Week ${week} aloud`,
    back: (week) => `Week ${week}`,
    open: 'Open',
  },
  scope: {
    title: 'What to practise',
    weeks: 'Weeks',
    allWeeks: 'All weeks',
    weeksPicked: (k) => `${k} ${k === 1 ? 'week' : 'weeks'}`,
    pickHint: 'Tap a week on the road to practise only that one.',
    kinds: 'Kinds of cards',
    kind: { 'hv-native': 'Words', 'native-hv': 'Into Valyrian', 'form-drill': 'Forms', sentence: 'Sentences', listen: 'Listening' },
    allKinds: 'all cards',
    change: 'Change',
    emptyFiltered: 'No cards for this choice yet.',
    showAll: 'Practise everything',
    empty: 'Free practice reviews words you have already seen.',
  },
  notebook: { show: 'Show', hide: 'Hide' },
};

// Messages.weekPlan (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('weekPlan', weekPlanFi, weekPlanEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof weekPlanFi> {}
}
